import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, ArrowRight, MailCheck } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import CinematicBackground from '../components/CinematicBackground';

export default function ForgotPassword() {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState('');
  const [devNotice, setDevNotice] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post('/auth/forgot-password', { email: email.trim().toLowerCase() });
      if (data?.dev_notice) setDevNotice(data.dev_notice);
      setSent(true);
    } catch {
      // Endpoint always returns 200 to prevent enumeration
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#040A08] text-foreground overflow-hidden" data-testid="forgot-root">
      <CinematicBackground intensity="dim" />
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6">
        <motion.button
          onClick={() => nav('/')}
          data-testid="btn-forgot-back"
          initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
          className="absolute top-6 left-6 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
        >
          <ArrowLeft className="w-3 h-3" /> Back
        </motion.button>

        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="w-full max-w-sm"
        >
          {!sent ? (
            <>
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[10px] uppercase tracking-[0.3em] text-primary-light">
                  <Mail className="w-3 h-3" /> Recover Access
                </div>
                <h1 className="mt-4 font-serif text-4xl leading-tight">Forgot Password</h1>
                <p className="mt-2 text-xs text-muted-foreground">
                  Enter the email you used to sign up. We&apos;ll send a reset link that&apos;s valid for 30 minutes.
                </p>
              </div>

              <form onSubmit={submit} className="space-y-3 p-6 rounded-3xl glass border border-white/10" data-testid="forgot-form">
                <input
                  data-testid="forgot-input-email"
                  type="email" autoComplete="email" required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 rounded-full bg-black/30 border border-white/10 text-sm outline-none focus:border-primary-light/60"
                />
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="forgot-submit"
                  className="w-full py-3 rounded-full bg-primary text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 hover:brightness-110"
                >
                  {busy ? 'Sending…' : 'Send reset link'}
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </form>
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              className="text-center p-8 rounded-3xl glass border border-white/10"
              data-testid="forgot-success"
            >
              <MailCheck className="w-12 h-12 mx-auto text-emerald-400" strokeWidth={1.4} />
              <h2 className="mt-4 font-serif text-2xl">Check your inbox</h2>
              <p className="mt-3 text-xs text-muted-foreground max-w-xs mx-auto">
                If an account exists for <span className="text-white/80 font-mono">{email}</span>, a password reset link has been sent. The link expires in 30 minutes.
              </p>
              {devNotice && (
                <div
                  data-testid="forgot-dev-notice"
                  className="mt-5 rounded-2xl border border-amber-400/40 bg-amber-500/10 px-4 py-3 text-left"
                >
                  <p className="text-[10px] uppercase tracking-widest text-amber-200 mb-1">Developer mode</p>
                  <p className="text-[11px] text-amber-100/90 leading-relaxed">{devNotice}</p>
                  <p className="text-[10px] text-amber-100/70 mt-2 font-mono">
                    grep &quot;reset-password?token&quot; /var/log/supervisor/backend.err.log
                  </p>
                </div>
              )}
              <button
                onClick={() => nav('/')}
                data-testid="forgot-return"
                className="mt-6 text-[10px] uppercase tracking-widest text-primary-light hover:text-foreground"
              >Return to home →</button>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
